<template>
  <ProductCardCarouselSkeleton
    v-if="
      recommendationsStatus === 'idle' || recommendationsStatus === 'pending'
    "
    :with-title="!!title"
  />
  <section
    v-else-if="recommendations.length"
    data-qa="recommendation-carousel"
    data-test="recommendation-carousel"
  >
    <ProductCardCarousel
      :products="recommendations"
      :subtitle
      :title
      :tracking="{ ...trackingData, provider: 'algolia' }"
      :use-client-side-navigation
      v-on="withAddToCart ? { addToCart: addToCartHandler } : {}"
    />
  </section>
</template>

<script lang="ts" setup>
import { watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'

import { useAlgoliaRecommendations } from '../composables/useAlgoliaRecommendations/useAlgoliaRecommendations'
import type {
  AlgoliaRecommendationsModel,
  AlgoliaRecommendationsObjectId,
  AlgoliaRecommendationsOptions,
} from '../composables/useAlgoliaRecommendations/useAlgoliaRecommendations.types'
import type { ExtraTrackingData } from '../composables/useProductTracking'

import ProductCardCarousel from './ProductCardCarousel/ProductCardCarousel.vue'
import ProductCardCarouselSkeleton from './ProductCardCarousel/ProductCardCarouselSkeleton.vue'
import translations from './Recommendation.translations'

const {
  title,
  subtitle,
  recommendationQuery,
  withAddToCart = false,
  trackingData = {},
  useClientSideNavigation = true,
  showSuccessToast = true,
} = defineProps<{
  recommendationQuery: {
    objectID: AlgoliaRecommendationsObjectId
    model: AlgoliaRecommendationsModel
    options?: AlgoliaRecommendationsOptions
  }
  title?: string
  subtitle?: string
  withAddToCart?: boolean
  trackingData?: ExtraTrackingData
  useClientSideNavigation?: boolean
  showSuccessToast?: boolean
}>()

const i18n = useI18n()
const { openSuccessToast, openErrorToast } = useTheToast()
const emit = defineEmits(['error', 'loaded', 'refresh', 'added-to-cart'])
const {
  data: recommendations,
  error: recommendationsError,
  status: recommendationsStatus,
} = useAlgoliaRecommendations(
  () => recommendationQuery.objectID,
  recommendationQuery.model,
  () => recommendationQuery.options,
)

function addToCartHandler({
  status,
  product,
}: {
  status: 'success' | 'error'
  product: { listingId: string }
}) {
  if (status === 'success') {
    if (showSuccessToast) {
      openSuccessToast({
        title: i18n(translations.toastSuccessTitle),
        content: i18n(translations.toastSuccessMessage),
      })
    }
    emit('refresh', product.listingId)
    emit('added-to-cart', product)
  }
  if (status === 'error') {
    openErrorToast({
      title: i18n(translations.toastErrorTitle),
      content: i18n(translations.toastErrorMessage),
    })
  }
}

watch(recommendationsStatus, () => {
  if (recommendationsStatus.value === 'success') {
    if (!recommendations.value.length) {
      emit('error', null)

      return
    }

    emit('loaded', recommendations.value)
  } else if (recommendationsStatus.value === 'error') {
    emit('error', recommendationsError.value)
  }
})
</script>
